import { useRef, useState, type FormEvent } from "react";

import { Button, Modal, Select } from "@/components/common";
import { extractErrorMessage } from "@/services/axiosConfig";
import { useResponsibleLookup, useUpdateClient } from "@/hooks/useClients";
import { type Client, TAX_REGIME_OPTIONS } from "@/types/client";
import { formatCpf } from "@/utils/fmt";

const REGIME_OPTIONS = [
  { value: "", label: "— Não definido —" },
  ...TAX_REGIME_OPTIONS.map((r) => ({ value: r, label: r })),
];

/** ARCH-002: modal canônico de EDIÇÃO de empresa. A criação tem um único modal
 * canônico (ClientCreateModal, com link/manual multi-CNPJ). Aqui só editamos uma
 * empresa existente — documento/organização são imutáveis e o payload espelha o
 * update do mesmo endpoint. */
type Props = { mode: "edit"; workspaceId: number; client: Client; onClose: () => void };

export function ClientFormModal({ workspaceId, client, onClose }: Props) {
  const updateMut = useUpdateClient(workspaceId, client.id);
  const respLookup = useResponsibleLookup(workspaceId);
  const lastCpf = useRef<string>("");

  const document = client.document_masked;
  const [legalName, setLegalName] = useState(client.legal_name);
  const [taxRegime, setTaxRegime] = useState(client.tax_regime ?? "");
  const [respCpf, setRespCpf] = useState(client.responsible_cpf_masked ?? "");
  const [respName, setRespName] = useState(client.responsible_name ?? "");
  const [linkedUser, setLinkedUser] = useState<{ id: number; name: string | null } | null>(
    client.responsible_user_id ? { id: client.responsible_user_id, name: client.responsible_name } : null,
  );
  const [error, setError] = useState<string | null>(null);

  const busy = updateMut.isPending;

  function onRespCpfChange(raw: string) {
    setRespCpf(formatCpf(raw));
    const digits = raw.replace(/\D/g, "");
    setLinkedUser(null);
    if (digits.length === 11 && digits !== lastCpf.current) {
      lastCpf.current = digits;
      respLookup.mutate(digits, {
        onSuccess: (data) => {
          if (data.found && data.user_id) setLinkedUser({ id: data.user_id, name: data.name });
        },
      });
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const respCpfDigits = respCpf.replace(/\D/g, "");
      // O CPF do responsável vem mascarado (`***.***.***-01` -> 2 dígitos);
      // só é um valor "novo" quando o usuário digitou um CPF completo (11 dígitos).
      const typedNewCpf = respCpfDigits.length === 11;
      const fieldEmptied = respCpf.trim() === "";
      const hadResponsible = client.responsible_user_id != null || client.responsible_cpf_masked != null;
      // Markers de limpeza: ao esvaziar um responsável antes preenchido, enviamos
      // os markers que o backend interpreta (responsible_cpf:'' / responsible_user_id:0)
      // para tornar acessível a remoção do responsável pela UI.
      const clearing = fieldEmptied && hadResponsible;
      await updateMut.mutateAsync({
        legal_name: legalName.trim(),
        tax_regime: taxRegime, // "" limpa o regime no backend
        responsible_cpf: linkedUser
          ? undefined
          : clearing
            ? "" // marker: limpa o CPF do responsável
            : typedNewCpf
              ? respCpfDigits
              : undefined,
        responsible_name: linkedUser ? undefined : respName.trim() || undefined,
        responsible_user_id: clearing ? 0 : undefined, // marker: remove o vínculo TDAX
      });
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      onClose={onClose}
      title="Editar empresa"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="client-form" disabled={busy}>
            {busy ? "Salvando…" : "Salvar"}
          </Button>
        </>
      }
    >
      <form
        id="client-form"
        onSubmit={onSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {error && <div className="form-error">{error}</div>}

        <label className="form-field">
          <span>CNPJ ou CPF</span>
          <input
            className="form-input"
            value={document}
            placeholder="00.000.000/0000-00"
            disabled
            readOnly
            inputMode="numeric"
          />
        </label>

        <label className="form-field">
          <span>Razão social / Nome</span>
          <input
            className="form-input"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            required
          />
        </label>

        <label className="form-field">
          <span>Regime tributário</span>
          <Select
            value={taxRegime || ""}
            onChange={(v) => setTaxRegime(v ?? "")}
            options={REGIME_OPTIONS}
          />
        </label>

        <label className="form-field">
          <span>CPF do responsável {respLookup.isPending && "· buscando…"}</span>
          <input
            className="form-input"
            value={respCpf}
            onChange={(e) => onRespCpfChange(e.target.value)}
            placeholder="000.000.000-00"
            inputMode="numeric"
          />
        </label>

        {linkedUser ? (
          <div className="td-small" style={{ color: "var(--ok, #16a34a)" }}>
            Vinculado ao usuário TDAX{linkedUser.name ? `: ${linkedUser.name}` : ""}
          </div>
        ) : (
          <label className="form-field">
            <span>Nome do responsável (externo)</span>
            <input
              className="form-input"
              value={respName}
              onChange={(e) => setRespName(e.target.value)}
              placeholder="Contador, sócio, advogado…"
            />
          </label>
        )}
      </form>
    </Modal>
  );
}
